/**
 * useTextSelection Hook
 *
 * Custom hook for handling text selection and share functionality.
 * Provides selection detection, context menu management, and markdown detection.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  hasMarkdownFormatting,
  extractMarkdownFromSelection,
} from './textSelectionUtils';

export function useTextSelection() {
  const [selectedText, setSelectedText] = useState('');
  const [selectedMarkdown, setSelectedMarkdown] = useState('');
  const [hasMarkdown, setHasMarkdown] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  const selectionTimeoutRef = useRef(null);
  const contextMenuRef = useRef(null);
  const lastSelectionRef = useRef('');
  const isActivelySelectingRef = useRef(false);
  const mouseDownTimeRef = useRef(0);
  const isTouchDeviceRef = useRef(false);
  const touchStartTimeRef = useRef(0);
  const touchMenuTimerRef = useRef(null);
  const touchInsideMenuRef = useRef(false);

  // Clear all selection state
  const clearSelection = useCallback(() => {
    setSelectedText('');
    setSelectedMarkdown('');
    setHasMarkdown(false);
    setIsSelecting(false);
    setSelectionRect(null);
    setContextMenuVisible(false);
    lastSelectionRef.current = '';
    
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
      selectionTimeoutRef.current = null;
    }
    if (touchMenuTimerRef.current) {
      clearTimeout(touchMenuTimerRef.current);
      touchMenuTimerRef.current = null;
    }
  }, []);

  // Show context menu for touch devices after selection stabilizes
  const showTouchContextMenu = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      touchMenuTimerRef.current = null;
      return;
    }

    const text = selection.toString().trim();
    if (text.length < 3 || text.length > 5000) {
      touchMenuTimerRef.current = null;
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      touchMenuTimerRef.current = null;
      return;
    }

    const markdown = extractMarkdownFromSelection(selection);
    const hasMarkdownFormat = hasMarkdownFormatting(markdown) || (markdown && markdown.includes('\n'));

    setSelectedText(text);
    setSelectedMarkdown(markdown);
    setHasMarkdown(hasMarkdownFormat);
    setSelectionRect({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom + window.scrollY,
      right: rect.right + window.scrollX,
    });
    setIsSelecting(true);
    lastSelectionRef.current = text;

    // Position menu above selection, or below if not enough space
    const menuX = rect.left + (rect.width / 2);
    const menuY = rect.top - 10;
    setContextMenuPosition({
      x: Math.max(20, Math.min(menuX, window.innerWidth - 200)),
      y: menuY < 60 ? rect.bottom + 10 : menuY,
    });
    setContextMenuVisible(true);
    touchMenuTimerRef.current = null;
  }, []);

  // Handle text selection
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();

    // On touch devices, reset the menu stabilization timer.
    // This lets users adjust selection handles without the menu appearing prematurely.
    if (isTouchDeviceRef.current && touchMenuTimerRef.current) {
      clearTimeout(touchMenuTimerRef.current);
      if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
        const selText = selection.toString().trim();
        if (selText.length >= 3) {
          touchMenuTimerRef.current = setTimeout(() => {
            showTouchContextMenu();
          }, 800);
        } else {
          touchMenuTimerRef.current = null;
        }
      } else {
        touchMenuTimerRef.current = null;
      }
    }

    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      // Don't clear during active selection to prevent interference
      if (isActivelySelectingRef.current) {
        return;
      }

      // Only clear if we had a selection before (don't clear during active selection)
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      selectionTimeoutRef.current = setTimeout(() => {
        // Double-check that selection is still collapsed and not actively selecting
        const currentSelection = window.getSelection();
        if (!isActivelySelectingRef.current && (!currentSelection || currentSelection.rangeCount === 0 || currentSelection.isCollapsed)) {
          clearSelection();
        }
      }, 150);
      return;
    }
    
    const text = selection.toString().trim();
    
    // Check if selection is within interactive elements (inputs, buttons, etc.)
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const parentElement = container.nodeType === Node.TEXT_NODE 
      ? container.parentElement 
      : container;
    
    // Skip if selection is in form elements or buttons
    if (parentElement && (
      parentElement.tagName === 'INPUT' ||
      parentElement.tagName === 'TEXTAREA' ||
      parentElement.tagName === 'BUTTON' ||
      parentElement.contentEditable === 'true' ||
      parentElement.closest('button, input, textarea, [contenteditable="true"]')
    )) {
      return;
    }
    
    // Ignore very short selections or very long selections (likely accidental)
    if (text.length < 3 || text.length > 5000) {
      return;
    }

    // If actively selecting, defer heavy processing to avoid interference
    if (isActivelySelectingRef.current) {
      // Just update the text for now, defer full processing
      lastSelectionRef.current = text;
      return;
    }

    // Extract markdown and position data immediately while selection is still valid
    const markdown = extractMarkdownFromSelection(selection);
    const hasMarkdownFormat = hasMarkdownFormatting(markdown) || (markdown && markdown.includes('\n'));
    const rect = range.getBoundingClientRect();
    
    // Only proceed if we have a valid rectangle
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    
    const selectionData = {
      text,
      markdown,
      hasMarkdown: hasMarkdownFormat,
      rect: {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom + window.scrollY,
        right: rect.right + window.scrollX,
      }
    };
    
    // Update immediately for responsiveness, but debounce the UI updates
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }
    
    // Only update if the selection has meaningfully changed
    if (Math.abs(text.length - lastSelectionRef.current.length) > 5 || 
        text !== lastSelectionRef.current) {
      
      lastSelectionRef.current = text;
      
      selectionTimeoutRef.current = setTimeout(() => {
        // Use the captured selection data instead of re-querying selection
        setSelectedText(selectionData.text);
        setSelectedMarkdown(selectionData.markdown);
        setHasMarkdown(selectionData.hasMarkdown);
        setSelectionRect(selectionData.rect);
        setIsSelecting(true);
        setContextMenuVisible(false); // Close context menu when new selection is made
      }, 200); // Reduced timeout for better responsiveness when not actively selecting
    }
  }, [clearSelection, showTouchContextMenu]);
  
  // Handle right-click context menu
  const handleContextMenu = useCallback((event) => {
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const text = selection.toString().trim();
      
      if (text.length >= 3 && text.length <= 5000) {
        event.preventDefault();
        
        // Clear any existing timeout to prevent conflicts
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
        }
        
        setContextMenuVisible(true);
        setContextMenuPosition({
          x: event.clientX,
          y: event.clientY,
        });
        
        // Update selection data immediately (no timeout for context menu)
        setSelectedText(text);
        const markdown = extractMarkdownFromSelection(selection);
        const hasMarkdownFormat = hasMarkdownFormatting(markdown) || (markdown && markdown.includes('\n'));
        setSelectedMarkdown(markdown);
        setHasMarkdown(hasMarkdownFormat);
      }
    }
  }, []);
  
  // Handle mouse events to track active selection
  const handleMouseDown = useCallback((event) => {
    // Only track on left mouse button
    if (event.button === 0) {
      isActivelySelectingRef.current = true;
      mouseDownTimeRef.current = Date.now();
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    // Add small delay to ensure selection has stabilized
    setTimeout(() => {
      isActivelySelectingRef.current = false;
    }, 100);
  }, []);

  // Handle touch events for mobile support
  const handleTouchStart = useCallback((event) => {
    isTouchDeviceRef.current = true;
    touchStartTimeRef.current = Date.now();

    // If touch is inside the context menu, let the button's click handler fire
    const isInsideMenu = contextMenuRef.current
      && contextMenuRef.current.contains(event.target);
    touchInsideMenuRef.current = isInsideMenu;

    if (isInsideMenu) {
      return; // Don't hide menu or start selection tracking
    }

    isActivelySelectingRef.current = true;
    // Cancel any pending menu display and hide existing menu
    if (touchMenuTimerRef.current) {
      clearTimeout(touchMenuTimerRef.current);
      touchMenuTimerRef.current = null;
    }
    setContextMenuVisible(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    // If touch was inside the menu, skip — let the click handler do its job
    if (touchInsideMenuRef.current) {
      touchInsideMenuRef.current = false;
      return;
    }

    // Small delay before clearing "actively selecting" flag
    setTimeout(() => {
      isActivelySelectingRef.current = false;
    }, 100);

    // Don't show context menu immediately on touch.
    // Start a stabilization timer — menu shows after selection hasn't
    // changed for 800ms, giving users time to adjust selection handles.
    if (touchMenuTimerRef.current) {
      clearTimeout(touchMenuTimerRef.current);
    }
    touchMenuTimerRef.current = setTimeout(() => {
      showTouchContextMenu();
    }, 800);
  }, [showTouchContextMenu]);

  // Handle clicks outside context menu and general clicks
  const handleClickOutside = useCallback((event) => {
    if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
      setContextMenuVisible(false);
    }

    // Skip clearing selection if user is actively selecting or just finished selecting
    if (isActivelySelectingRef.current || (Date.now() - mouseDownTimeRef.current) < 200) {
      return;
    }

    // Clear selection when clicking outside of text content (with a small delay to allow for new selections)
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        clearSelection();
      }
    }, 50);
  }, [clearSelection]);
  
  // Hide selection UI
  const hideSelection = useCallback(() => {
    clearSelection();
    
    // Clear the actual browser selection
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  }, [clearSelection]);
  
  // Set up event listeners
  useEffect(() => {
    // Add error handling for event listeners
    const safeHandleSelectionChange = (event) => {
      try {
        handleSelectionChange(event);
      } catch (error) {
        console.warn('Text selection handler error:', error);
      }
    };
    
    const safeHandleContextMenu = (event) => {
      try {
        handleContextMenu(event);
      } catch (error) {
        console.warn('Context menu handler error:', error);
      }
    };
    
    const safeHandleClickOutside = (event) => {
      try {
        handleClickOutside(event);
      } catch (error) {
        console.warn('Click outside handler error:', error);
      }
    };
    
    document.addEventListener('selectionchange', safeHandleSelectionChange);
    document.addEventListener('contextmenu', safeHandleContextMenu);
    document.addEventListener('click', safeHandleClickOutside);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('selectionchange', safeHandleSelectionChange);
      document.removeEventListener('contextmenu', safeHandleContextMenu);
      document.removeEventListener('click', safeHandleClickOutside);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);

      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      if (touchMenuTimerRef.current) {
        clearTimeout(touchMenuTimerRef.current);
      }
    };
  }, [handleSelectionChange, handleContextMenu, handleClickOutside, handleMouseDown, handleMouseUp, handleTouchStart, handleTouchEnd]);
  
  return {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    isSelecting,
    selectionRect,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  };
}